import type { DefaultProps } from "@aerstudios/aerated-types/types";
import {
	type ValidityAttributes,
	handleValidityMessage,
	validValidityState,
} from "@aerstudios/aerated-utilities/formHelpers";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { Slot } from "@radix-ui/react-slot";
import cx from "clsx";
import React, {
	forwardRef,
	type JSX,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type ElementRef,
	type ElementType,
	type ForwardedRef,
	type HTMLAttributes,
	type ReactNode,
} from "react";
import * as styles from "./AerInputStructure.module.scss";

const normalisedStyles = "default" in styles ? styles.default : styles;

const returnAttributeOrEmpty = (
	attributeName: string,
	values?: string[],
): Record<string, string> => {
	const combinedValues = values?.filter((id) => !!id).join(" ");
	const attribute: Record<string, string> = {};

	if (combinedValues) {
		attribute[attributeName] = combinedValues;
	}

	return attribute;
};

/**
 * If a value is passed but no way to respond to changes, then show an error.
 */
function validateProps(props: InputSharedProps): void {
	// todo: check for a label in the children
	if (!props.handleChange && typeof props.value !== "undefined") {
		console.warn(
			"Controlled input error: If you pass a value prop, you must also pass a handleChange prop.",
		);
	}
}

function validateInputProps(
	_props: Omit<InputProps, "inputElement">,
	_id?: string,
): void {
	// todo: check for a label in the children or aria-labelledby or aria-label
}

export const canCheckOwnValidity = (
	input: unknown,
): input is HTMLInputElement =>
	!!input &&
	typeof input === "object" &&
	"checkValidity" in input &&
	"validity" in input;

// type for a function that accepts and input, a textarea, or a select and returns a ValidityState
export type CustomValidityFunction = (
	input: ValidityAttributes,
) => ValidityState;

export function checkInputValidity(
	input?: ValidityAttributes | undefined | null,
	customValidationFunction?: CustomValidityFunction,
): ValidityState {
	if (!input) {
		return validValidityState;
	}

	if (customValidationFunction) {
		return customValidationFunction(input);
	}

	if (canCheckOwnValidity(input)) {
		input.checkValidity();

		return input.validity;
	}

	return validValidityState;
}

export type LabelProps = DefaultProps<"label"> & {
	/** If you need the label to be visually hidden (but still available to screen readers), set this to `true` */
	visuallyHidden?: boolean;
};

/**
 * The label for the input. It is optional, but if you don't provide one, you must provide an `aria-label` or `aria-labelledby` attribute to the input. We recommend using this component, as it will provide a consistent experience and you can use the `visuallyHidden` prop to hide it if necessary.
 */
export const AerLabel = forwardRef(
	(
		{ children, visuallyHidden = false, className, ...rest }: LabelProps,
		ref: React.ForwardedRef<HTMLLabelElement>,
	) => {
		const { id } = useAerInputStructureContext();

		return (
			<label
				ref={ref}
				htmlFor={id}
				className={cx(
					normalisedStyles.label,
					{
						[normalisedStyles.hiddenLabel]: visuallyHidden,
					},
					className,
				)}
				{...rest}
			>
				{children}
			</label>
		);
	},
);

export type AerInputMessageProps = DefaultProps<"div"> & {
	/** Type of message. Defaults to "help" */
	type?: "help" | "error";
};

export const AerInputMessage = forwardRef(
	(
		{ children, type = "help", className, ...rest }: AerInputMessageProps,
		ref: React.ForwardedRef<HTMLDivElement>,
	) => {
		const { id, setHasHelpText } = useAerInputStructureContext();
		// NOTE: this `id` is a formal pattern of inputId + "-message". It will be overridden if there is id prop (as per the ErrorMessage).
		const helpId = `${id}-message`;

		const extraProps: HTMLAttributes<HTMLDivElement> = { ...rest };

		if (type === "error") {
			extraProps.role = "alert";
			setHasHelpText(undefined);
		}

		useEffect(() => {
			if (type !== "error") {
				setHasHelpText(helpId);
			}
		}, [helpId, setHasHelpText, type]);

		if (!children) {
			return null;
		}
		return (
			<div
				{...extraProps}
				ref={ref}
				className={cx(normalisedStyles[`${type}Text`], className)}
				id={helpId}
			>
				{children}
			</div>
		);
	},
);

export const AerInputErrorMessage = forwardRef(
	(
		{ className, ...rest }: AerInputMessageProps,
		ref: React.ForwardedRef<HTMLDivElement>,
	) => {
		const { errorMessage, id } = useAerInputStructureContext();

		return (
			<AerInputMessage
				{...rest}
				type="error"
				ref={ref}
				className={className}
				id={`${id}-error`}
			>
				{errorMessage}
			</AerInputMessage>
		);
	},
);

export type AerCharacterCountProps = DefaultProps<"span"> & {
	maxCount: number;
	prefix?: string;
	suffix?: string;
	inter?: string;
};

export const AerCharacterCount = forwardRef(
	(
		{
			maxCount,
			prefix = "",
			suffix = "",
			inter = "/",
			className,
			...rest
		}: AerCharacterCountProps,
		ref: React.ForwardedRef<HTMLSpanElement>,
	) => {
		const { value } = useAerInputStructureContext();
		const currentCount = value?.trim().length ?? 0;

		const ariaLabel = returnAttributeOrEmpty("aria-label", [
			!(prefix && suffix)
				? `${currentCount} ${
						inter === "/" ? "of" : inter
					} ${maxCount} characters used`
				: "",
		]);
		const ariaHidden = returnAttributeOrEmpty("aria-hidden", [
			!(prefix && suffix) ? "true" : "",
		]);

		return (
			<span
				aria-live="polite"
				className={cx(normalisedStyles.characterCount, className)}
				ref={ref}
				{...ariaLabel}
				{...rest}
			>
				<span
					{...ariaHidden}
				>{`${prefix}${currentCount}${inter}${maxCount}${suffix}`}</span>
			</span>
		);
	},
);

export type ValidityFormHandler = (
	value: string | undefined | null,
	validity?: ValidityState,
) => void;

interface InputSharedProps {
	/** If a specific `id` is needed, you can provide one, otherwise `useId` one will be used */
	id?: string;
	/** An initial value. Use this to provide a value without needing to provide `value` and control functions. Local state will override this. */
	initialValue?: string;
	/** The value that should be applied, this overrides local state management so you will need to use the `handleChange` prop to manage state */
	value?: string;
	/** To control the component, provide a `handleChange` function AND a `value` */
	handleChange?: ValidityFormHandler;
	/** Calls on blur event; it will allow control of behaviour only when the user blurs the "input" element */
	handleBlur?: ValidityFormHandler;
	/** Display an error message and set the input to invalid (this overrides the actual validity of the field). A falsy value will be treated as no error */
	errorMessage?: ReactNode;
	/** Trigger a validity check. */
	forceValidityCheck?: boolean;
	/** Show help in using the component, it is recommended that you set this to be true based on environment */
	debug?: boolean;
	/** A custom validity check function - this is necessary for situations where you want to provide validation, but the element doesn't support `ValidityState` */
	customValidationFunction?: CustomValidityFunction;
}

export type InputStructureProps = DefaultProps<"div"> & InputSharedProps;

export interface AerInputStructureContextValue extends InputSharedProps {
	/** Function that calls change events and custom change functions */
	handleChange: ValidityFormHandler;
	/** Function that triggers blurs and custom blur functions */
	handleBlur: ValidityFormHandler;
	/** The wrapper needs to know about the enabled state of the input */
	setIsDisabled: (isDisabled: boolean) => void;
	/** A function to set the contents of the errorMessage from within the context */
	setErrorMessage: (errorMessage: ReactNode) => void;
	/** If there is an error, this will contain the message for that error */
	errorMessage: ReactNode;
	/** The passed in custom validation function */
	customValidationFunction?: CustomValidityFunction;
	/** Whether component usage validation is turned on */
	debug?: boolean;
	/** Called when help text is rendered */
	setHasHelpText: (hasHelpText: string | undefined) => void;
	/** A space separated string of help text ids */
	hasHelpText: string | undefined;
}

const AerInputStructureContext =
	React.createContext<AerInputStructureContextValue | null>(null);

export const useAerInputStructureContext = () => {
	const context = React.useContext(AerInputStructureContext);
	if (!context) {
		throw new Error(
			"`AerInputStructure` sub-components must be used within an AerInputStructure.Root component",
		);
	}

	return context;
};

export type InputProps<Tagname extends ElementType = ElementType> =
	DefaultProps<Tagname> & {
		/** Optional SVGElement or image to show when the element is invalid. This has a default icon, so if you need to hide it, set this to `null` */
		ErrorIcon?: ReactNode;
		/** Optional className to add to the wrapping element */
		wrapperClassName?: string;
	};

export type allowedTagnames = "input" | "button" | "textarea" | "select";

// A utility type to extract the HTML element type based on the provided Tagname
export type InferElementType<Tagname extends ElementType> =
	Tagname extends keyof Pick<JSX.IntrinsicElements, allowedTagnames>
		? JSX.IntrinsicElements[Tagname]
		: HTMLElement; // Default to HTMLElement if the Tagname doesn't match any known HTML element.

export const AerInput = forwardRef(
	<Tagname extends ElementType>(
		{
			className,
			wrapperClassName,
			asChild,
			ErrorIcon = <ExclamationTriangleIcon />,
			...rest
		}: InputProps<Tagname>,
		ref: ForwardedRef<ElementRef<Tagname>>,
	) => {
		const inputElementRef =
			(ref as React.RefObject<ElementRef<Tagname> & ValidityAttributes>) ??
			useRef<ElementRef<Tagname> & ValidityAttributes>(null);

		const {
			id,
			forceValidityCheck,
			value,
			errorMessage,
			handleBlur,
			handleChange,
			setIsDisabled,
			setErrorMessage,
			customValidationFunction,
			hasHelpText,
			debug,
		} = useAerInputStructureContext();

		debug && validateInputProps(rest, id);

		// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
		const onChange = useCallback(
			(e: React.ChangeEvent<ElementRef<Tagname> & ValidityAttributes>) => {
				const target = (inputElementRef?.current ??
					e.target) as ElementRef<Tagname> & ValidityAttributes;
				const { value } = target;
				const validity = checkInputValidity(e.target, customValidationFunction);

				setErrorMessage(
					handleValidityMessage(target, validity, forceValidityCheck),
				);

				handleChange(value, validity);
			},
			[forceValidityCheck],
		);

		// biome-ignore lint/correctness/useExhaustiveDependencies: forceValidityCheck
		const onBlur = useCallback(
			(e: React.FocusEvent<ElementRef<Tagname> & ValidityAttributes>) => {
				const target = (inputElementRef?.current ??
					e.target) as ElementRef<Tagname> & ValidityAttributes;
				const { value } = target;
				const validity = checkInputValidity(e.target, customValidationFunction);

				setErrorMessage(
					// always trigger a validity check on blur
					handleValidityMessage(target, validity, true),
				);

				handleBlur(value, validity);
			},
			[
				forceValidityCheck,
				handleBlur,
				setErrorMessage,
				customValidationFunction,
				inputElementRef.current,
			],
		);

		useEffect(() => {
			if (forceValidityCheck) {
				let validity: ValidityState | undefined = undefined;

				if (inputElementRef?.current) {
					validity = checkInputValidity(
						inputElementRef.current,
						customValidationFunction,
					);
				}

				setErrorMessage(
					handleValidityMessage(
						inputElementRef?.current,
						validity,
						forceValidityCheck,
					),
				);
			}
		}, [
			forceValidityCheck,
			setErrorMessage,
			customValidationFunction,
			inputElementRef.current,
		]);

		useLayoutEffect(() => {
			setIsDisabled(!!rest.disabled || rest["aria-disabled"] === "true");
		}, [rest.disabled, rest["aria-disabled"], setIsDisabled]);

		const helpTextId = hasHelpText ?? "";
		const errorId = errorMessage ? `${id}-error` : "";
		const describedBy = returnAttributeOrEmpty("aria-describedby", [
			helpTextId,
			errorId,
		]);

		return (
			<span className={cx(normalisedStyles.inputWrap, wrapperClassName)}>
				<Slot
					{...rest}
					id={id}
					className={cx(
						normalisedStyles.input,
						{
							[normalisedStyles.error]: errorMessage,
						},
						className,
					)}
					ref={inputElementRef}
					onChange={onChange}
					onBlur={onBlur}
					value={value}
					aria-invalid={!!errorMessage}
					{...describedBy}
				/>
				{errorMessage && (
					<span className={normalisedStyles.errorIcon} aria-hidden>
						{ErrorIcon}
					</span>
				)}
			</span>
		);
	},
);

/**
 * Almost all inputs use exactly the same pattern of Label,
 * optional help text, error message behaviour, character
 * counts, etc. This set of Components provides a way to compose these behaviours and elements.
 *
 * ### What's in the box?
 *
 * AerInputStructure is made up of the following components:
 * 1. AerInputStructure.Root - this is the wrapper that provides the context, behaviour and control for the other components.
 * 2. AerInputStructure.Label - this is the label for the input. It is optional, but if you don't provide one, you must provide an `aria-label` or `aria-labelledby` attribute to the input.
 * 1. AerInputStructure validation, including behaviour on when to show the error messages. To trigger validation you must include an attribute such as `required`, `minlength`, `pattern`, etc. There is a pattern exported for an allow list of all letters and numbers plus \`_,()?/@&£€$:"'.-\`, which is a good balance between flexible and secure.
 * 2. Accessible inputs, including the option to accessibly visually hide labels. Due to the fact that you can provide any element as an input and choose to provide an `AerInputStructure.Label`, this is managed by the `debug` prop.
 * 3. Optional "help text" (AerInputStructure.InputMessage) in a consistent pattern and consistent behaviour.
 * 3. Optional "Error text" (AerInputStructure.ErrorMessage) this will automatically display any errors based around a predefined set of behaviours.
 * 4. Optional character counts - these need a \`maxCount\` prop that matches the input's \`maxLength\` prop.
 * 5. Consistent styling and behaviour.
 *
 * ### Control options
 * Apart from passing in the props for the input itself, there are a few ways to control the behaviour of the input:
 * 1. `handleBlur`, `handleChange`; both of these call the provided function with the current value and the current `ValidityState` of the input.
 * 2. `value` prop; if provided, this will set the value of the input, so it will have to be used in conjunction with `handleChange` to update the value.
 * 3. `errorMessage` prop; if provided, this will output the provided string and override the default error message if one were to be shown.
 * 4. `forceValidityCheck` prop; if provided, this will trigger a validity check on the input, and show the error message if one were to be shown.
 * 5. `customValidationFunction` prop; if provided, this will be called with the input element and the current value, and the return value will be used as the `ValidityState` of the input.
 * 6. `initialValue` prop; if provided, this will be used as the initial value of the input. This is useful for when you want to set the value of the input to something other than an empty string.
 */

export const AerInputStructure = forwardRef(
	(props: InputStructureProps, ref: React.ForwardedRef<HTMLDivElement>) => {
		props.debug && validateProps(props);

		const {
			children,
			className,
			debug = props.debug,
			errorMessage = "",
			value,
			forceValidityCheck = false,
			id,
			// todo is it better DX to add the change and blur to the input or the root?
			handleChange,
			handleBlur,
			customValidationFunction,
			...rest
		} = props;

		const [localValue, setLocalValue] = useState<string>(
			props.initialValue ?? "",
		);
		const [isDisabled, setIsDisabled] = useState(false);
		const [hasHelpText, setHasHelpText] = useState<string | undefined>(
			undefined,
		);
		const [errorMessageState, setErrorMessage] =
			useState<React.ReactNode>(errorMessage);

		const fallbackId = useId();
		const normalisedId = id ?? fallbackId;
		const normalisedErrorMessage = errorMessage || errorMessageState;

		const handleSetErrorMessage = useCallback((message: React.ReactNode) => {
			setIsValid(!message);
			setErrorMessage(message);
		}, []);

		const [isValid, setIsValid] = useState(!normalisedErrorMessage);

		const manageBlur = useCallback<ValidityFormHandler>(
			(value, validity) => {
				setIsValid(validity ? validity.valid : !normalisedErrorMessage);

				handleBlur?.(value, validity);
			},
			[handleBlur, normalisedErrorMessage],
		);

		const manageChange = useCallback<ValidityFormHandler>(
			(value, validity) => {
				setIsValid(
					validity ? !validity.patternMismatch : !normalisedErrorMessage,
				);

				const normalisedValue = value ?? "";

				setLocalValue(normalisedValue);

				handleChange?.(normalisedValue, validity);
			},
			[handleChange, normalisedErrorMessage],
		);

		// todo add check to warn if no label component & not `aria-labelledby`
		// biome-ignore lint/correctness/useExhaustiveDependencies: <todo: not sure about all the requirements here, needs looking at>
		const contextValue: AerInputStructureContextValue = useMemo(() => {
			return {
				setIsDisabled,
				forceValidityCheck,
				id: normalisedId,
				handleBlur: manageBlur,
				handleChange: manageChange,
				value: value ?? localValue,
				errorMessage: normalisedErrorMessage,
				setErrorMessage: handleSetErrorMessage,
				customValidationFunction,
				hasHelpText,
				setHasHelpText,
			};
		}, [
			normalisedErrorMessage,
			normalisedId,
			value,
			localValue,
			forceValidityCheck,
			hasHelpText,
		]);

		return (
			<AerInputStructureContext.Provider value={contextValue}>
				<div
					ref={ref}
					className={cx(normalisedStyles.component, className, {
						[normalisedStyles.disabled]: isDisabled,
						[normalisedStyles.wrapperError]: !isValid,
					})}
					aria-atomic="false"
					{...rest}
				>
					{children}
				</div>
			</AerInputStructureContext.Provider>
		);
	},
);
